import { mathService, miscHelpers } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { isSwiftPaymentMethod } from 'pages/requestForm/containers/xero/XeroAmaxPayBatchPaymentForm/XeroAmaxPayBatchPaymentForm.helpers';
import { CountryIsoCode, CurrencyIsoCode } from 'shared/data/walletsKyc/v1';
import { CheckAmaxPayPaymentDetailsDiscrepancies } from 'shared/data/webApp/v1';
import { CheckPaymentDetails } from 'shared/data/webApp/v2';

import { SupplierWithItems } from './BillsInformation.types';

export const createEmptySupplierWithItems = (): SupplierWithItems => ({
    items: [],
    total: {
        amountDue: 0,
        paymentAmount: 0,
        currency: '',
        contact: { id: '', text: '' },
        paymentDetailsName: '',
        paymentDetailsStatus: domain.PaymentDetailsPaymentStatus.NotChecked,
        paymentDetailsId: '',
        firstBillRequestId: '',
    },
});

export const updateSupplierWithItems = (
    supplier: SupplierWithItems,
    item: domain.AmaxPayPaymentItem,
    paymentDetailsDiscrepanciesOnChecking: CheckAmaxPayPaymentDetailsDiscrepancies['response']['items'] | null,
    paymentDetailsDiscrepanciesOnCheckingV2?: CheckPaymentDetails['response']['items'] | null
) => {
    if (item.contact) {
        const itemAmountDue = calculateItemAmountDue(item);

        supplier.items.push(item);
        supplier.total.amountDue = mathService.add(supplier.total.amountDue, itemAmountDue);
        supplier.total.paymentAmount += item.amount;
        supplier.total.currency = item.currency;
        supplier.total.contact = item.contact;
        supplier.total.paymentDetailsName = item.paymentDetails?.bankAccountDetails.bankAccount.bankAccountName || '';
        supplier.total.paymentDetailsStatus =
            item.paymentDetailsPaymentStatus ?? domain.PaymentDetailsPaymentStatus.NotChecked;
        supplier.total.paymentDetailsId = item.paymentDetails?.id || '';
        supplier.total.firstBillRequestId = item.xeroBillInvoiceRequestId;
        supplier.total.paymentDetailsStatus = getItemPaymentDetailsStatus(
            item,
            paymentDetailsDiscrepanciesOnChecking,
            paymentDetailsDiscrepanciesOnCheckingV2
        );
    }
};

const calculateItemAmountDue = (item: domain.AmaxPayPaymentItem) => {
    // INFO: li.amountDue - (li.amountInProcessing + li.amountOnApproval + li.amountAwaitingPayment)
    return mathService.subtract(
        item.amountDue,
        mathService.add(
            mathService.add(item.amountInProcessing ?? 0, item.amountOnApproval ?? 0),
            item.amountAwaitingPayment ?? 0
        )
    );
};

const getItemPaymentDetailsStatus = (
    item: domain.AmaxPayPaymentItem,
    paymentDetailsDiscrepanciesOnChecking: CheckAmaxPayPaymentDetailsDiscrepancies['response']['items'] | null,
    paymentDetailsDiscrepanciesOnCheckingV2?: CheckPaymentDetails['response']['items'] | null
) => {
    const statusV1 = paymentDetailsDiscrepanciesOnChecking?.find(
        (discrepancy) =>
            discrepancy.contactId === item.contact?.id &&
            discrepancy.paymentDetailsDiscrepancy.paymentDetailsId === item.paymentDetails?.id
    )?.paymentDetailsPaymentStatus;

    const statusV2 = paymentDetailsDiscrepanciesOnCheckingV2?.find(
        (discrepancy) =>
            discrepancy.contactId === item.contact?.id &&
            discrepancy.paymentDetailsDiscrepancy?.paymentDetailsId === item.paymentDetails?.id
    )?.paymentDetailsPaymentStatus;

    return (
        statusV1 ||
        miscHelpers.castToEnum(domain.PaymentDetailsPaymentStatus, statusV2) ||
        domain.PaymentDetailsPaymentStatus.NotChecked
    );
};

/**
 * @see https://support.currencycloud.com/hc/en-gb/articles/360017430000-Payment-Purpose-Codes
 */
export const isPaymentPurposeCodeConditionsMet = (
    paymentDetails: domain.AmaxPayPaymentItem['paymentDetails'],
    paymentCurrency: string
) => {
    if (!paymentDetails) {
        return false;
    }

    if (
        paymentDetails.bankAccountDetails.bankAccount.countryIsoCode === CountryIsoCode.BH &&
        isSwiftPaymentMethod(paymentDetails.payNetworkName)
    ) {
        return true;
    }

    if (
        paymentDetails.bankAccountDetails.bankAccount.countryIsoCode === CountryIsoCode.CN &&
        isSwiftPaymentMethod(paymentDetails.payNetworkName) &&
        paymentCurrency === 'CNH'
    ) {
        return true;
    }

    if (
        paymentDetails.bankAccountDetails.bankAccount.countryIsoCode === CountryIsoCode.IN &&
        // TODO change to `PayNetworkType` enum after BE will create a new `payNetworkType` field
        paymentDetails.payNetworkName === 'IMPS' &&
        paymentCurrency === CurrencyIsoCode.INR
    ) {
        return true;
    }

    if (
        paymentDetails.bankAccountDetails.bankAccount.countryIsoCode === CountryIsoCode.MY &&
        // TODO change to `PayNetworkType` enum after BE will create a new `payNetworkType` field
        paymentDetails.payNetworkName === 'IBG' &&
        paymentCurrency === CurrencyIsoCode.MYR
    ) {
        return true;
    }

    if (
        paymentDetails.bankAccountDetails.bankAccount.countryIsoCode === CountryIsoCode.AE &&
        isSwiftPaymentMethod(paymentDetails.payNetworkName)
    ) {
        return true;
    }

    return false;
};

export const getIsInvalidFactory = (isWalletsPaymentSource: boolean) => (lineItem: domain.AmaxPayPaymentItem) => {
    return isWalletsPaymentSource && !lineItem.virtualAccountDetails?.amaxPayWallet?.id;
};
